import React, { FunctionComponent, useEffect, useState } from 'react';
import { ColumnDefinition, RegularTitle, RowDefinition, Table } from '@indigo-ag/component-module';
import { AgGroupConnection, AgGroup } from '@indigo-ag/schema';
import { Link } from 'react-router-dom';

import { getAgGroupList } from 'features/brazil-grower/queries';
import {
  LoadingWrapper,
  TableSort,
  defaultPagination,
  date,
  TABLE_ACTIONS,
  calculateAfterPagination,
  currency,
  text,
  localStorageUtils,
  GroupListsFilterCriteria,
  defaultSortingBehavior
} from 'shared';
import i18next from 'core/i18n';
import { PillGroupIds, TabGroupIds } from 'features/brazil-grower/pages/tabs/groupConfig';
import { NegotiationsList } from '../shared/NegotiationsList/NegotiationsList';

interface GroupCessionListProps {
  activePill: PillGroupIds;
  activeTab: TabGroupIds;
}

type ColumnId = 'id' | 'cessionDate' | 'totalContractValue';

const columnDefinition: ColumnDefinition<AgGroup, ColumnId>[] = [
  {
    id: 'id',
    header: text.firstLetterUppercase(i18next.t('groupCession')),
    cell: ({ row }) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Link to={`/cessions/${row.id}`}>
          <RegularTitle style={{ fontWeight: 400, color: 'inherit' }}>{`#${row.id}`}</RegularTitle>
        </Link>
      </div>
    ),
    constraints: {
      min: { rem: 20 },
      max: { fr: 1 }
    }
  },
  {
    id: 'cessionDate',
    header: text.firstLetterUppercase(i18next.t('cessionDate')),
    cell: ({ row, SimpleCell }) => <SimpleCell label={date.format({ date: row.cessionDate || date.todayAt00() })} />,
    constraints: {
      min: { rem: 20 },
      max: { fr: 1 }
    }
  },
  {
    id: 'totalContractValue',
    header: text.firstLetterUppercase(i18next.t('totalContractValue')),
    cell: ({ row, SimpleCell }) => (
      <SimpleCell label={row.totalContractValue ? currency.formatBrazilCurrency(row.totalContractValue) : '-'} />
    ),
    constraints: {
      min: { rem: 20 },
      max: { fr: 1 }
    }
  }
];

const rowDefinition: RowDefinition<AgGroup, ColumnId> = {
  rowId: ({ row }) => row.id.toString(),
  expandable: ({ ColumnSpan, row }) => {
    return (
      <ColumnSpan columnEnd={5}>
        <div style={{ maxHeight: '45rem', overflowY: 'auto', padding: '2px' }}>
          <NegotiationsList negotiations={row.agFinancingProcesses} />
        </div>
      </ColumnSpan>
    );
  }
};

export const GroupCessionList: FunctionComponent<GroupCessionListProps> = ({ activeTab, activePill }) => {
  /**
   * state
   */
  const [sort, setSort] = useState<TableSort | null>(null);
  const [currentPage, setCurrentPage] = useState(() =>
    defaultPagination.currentPage(GroupListsFilterCriteria.CURRENT_GROUP_PAGE)
  );
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  /**
   * active pill effect
   */
  useEffect(() => {
    localStorageUtils.set(GroupListsFilterCriteria.CURRENT_GROUP_PAGE, currentPage);
    if (activePill) {
      localStorageUtils.set(GroupListsFilterCriteria.ACTIVE_GROUP_PILL, activePill);
    }
  }, [activePill, currentPage]);

  const renderAssignments = ({ data }: { data: { getAgGroupList: AgGroupConnection } }) => {
    const allEdges: AgGroup[] = data?.getAgGroupList?.edges?.map(e => e.node);
    const pagination = {
      limit: defaultPagination.rowsPerPage,
      offset: currentPage * defaultPagination.rowsPerPage,
      total: data?.getAgGroupList?.totalCount || 0
    };

    const handleTableAction = (action: any) => {
      const { type, payload } = action;

      switch (type) {
        case TABLE_ACTIONS.NEXT_PAGE:
          return setCurrentPage(currentPage + 1);
        case TABLE_ACTIONS.PREVIOUS_PAGE:
          return setCurrentPage(currentPage - 1);
        case TABLE_ACTIONS.ROW_EXPANDED:
          return payload ? setExpandedRows([...expandedRows, payload]) : null;
        case TABLE_ACTIONS.ROW_COLLAPSED:
          return setExpandedRows(expandedRows.filter(id => id !== payload));
        case TABLE_ACTIONS.SORT_CHANGED:
          setCurrentPage(0);
          return defaultSortingBehavior(payload, setSort, sort);
      }
    };

    return (
      <Table
        columnDefinition={columnDefinition}
        data={allEdges}
        expandedRows={expandedRows}
        label="AgFinance Assignments Queue"
        onTableAction={handleTableAction}
        pagination={pagination}
        paginationStyle="prev-next"
        rowDefinition={rowDefinition}
        sort={sort || undefined}
        sortable={['id', 'cessionDate']}
      />
    );
  };

  const getFilters = (activeTab: TabGroupIds, activePill: PillGroupIds) => {
    if (activeTab === TabGroupIds.CESSION_TO_FUND) {
      return {
        awaitingAssignment: activePill === PillGroupIds.NEXT_CESSION,
        assignedToFund: activePill === PillGroupIds.SENT_TO_FOUND,
        receivedByFund: activePill === PillGroupIds.RECEIVED_BY_FUND,
        awaitingManagerApproval: activePill === PillGroupIds.AWAITING_APPROVAL,
        managerDenied: activePill === PillGroupIds.REJECTED_APPROVAL,
        submittedCession: activePill === PillGroupIds.CESSION_ASSIGNED
      };
    }

    return {};
  };

  return (
    <LoadingWrapper
      query={getAgGroupList}
      queryOptions={{
        variables: {
          pagination: { first: defaultPagination.rowsPerPage, after: calculateAfterPagination(currentPage) },
          filters: getFilters(activeTab, activePill),
          sort: sort ? { id: sort.id, direction: sort.direction?.toUpperCase() } : sort
        },
        fetchPolicy: 'network-only'
      }}
    >
      {renderAssignments}
    </LoadingWrapper>
  );
};
